import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { ISnowComputersState } from './snowComputers.model';
import {
  deleteSnowComputers,
  getSnowComputersById,
  saveSnowComputers,
  searchSnowComputers,
} from './snowComputers.action';
import { ISnowComputers } from '../../../services/snow/snowComputers/snowComputers.model';

export const initialState: ISnowComputersState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const snowComputersSlice = createSlice({
  name: 'snowComputers',
  initialState,
  reducers: {
    clearSnowComputers: () => {
      return initialState;
    },
    clearSnowComputersMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearSnowComputersGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchSnowComputers.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchSnowComputers.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchSnowComputers.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getSnowComputersById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getSnowComputersById.fulfilled.type]: (
      state,
      action: PayloadAction<ISnowComputers>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getSnowComputersById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveSnowComputers.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveSnowComputers.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveSnowComputers.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteSnowComputers.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteSnowComputers.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteSnowComputers.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const snowComputersSelector = (state: RootState) => state.snowComputers;

// Actions
export const {
  clearSnowComputers,
  clearSnowComputersMessages,
  clearSnowComputersGetById,
  setTableColumnSelection,
} = snowComputersSlice.actions;

// The reducer
export default snowComputersSlice.reducer;
